/**
 * 电子围栏
 */
import React, { useEffect, useRef, useState } from 'react';
import { message, Input, Pagination, Button } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import NP from 'number-precision';
import setPng from '@/assets/image/set.png';
import {
  importDrawingManagerMap,
  calcAnotherPoint
} from '@/utils/map';
import { getMiddleAngle, getMinMaxAngle } from '@/utils/math';
import { useDebounce } from '@/utils/ownHooks';
import { registerClipboard } from '@/utils/common';
import { defaultMaxRadius } from '@/constants/electronFence';
import LoadingWrapper from '@/components/loadingWrapper';
import PermissionButton from '@/components/permissionButton';
import {
  fenceAreaDetailRequest,
  upsertFencePointRequest,
  deleteFencePointRequest,
  publishFenceAreaRequest,
  batchUpsertFencePointRequest,
  lngLatTransformRequest
} from '@/service/electronFence';
import FirmwareAddModal from '@/pages/firmwareManage/components/addModal';
import DownloadModal from './components/downloadModal';
import FenceConfigModal from './components/fenceConfigModal';
import FenceAreaList from './components/fenceAreaList';
import styles from '@/pages/crossMark/index.module.scss';

// 百度地图计算两点航向角
const calcLatLngAngleBMap = (map: any, start: any, end: any) => {
  const p_start = map.current.pointToPixel(start);
  const p_end = map.current.pointToPixel(end);
  const diff_x = p_end.x - p_start.x;
  const diff_y = p_end.y - p_start.y;
  let angle = Math.round((360 * Math.atan2(diff_y, diff_x)) / (2 * Math.PI) + 90);
  if (angle < 0) {
    angle += 360;
  }
  return angle;
};

const ElectronFence = () => {
  const [loading, setLoading] = useState(false); // loading
  const [selectFenceAreaId, setSelectFenceAreaId] = useState<any>(null); // 选择的围栏区域ID
  const [selectFenceAreaPointCount, setSelectFenceAreaPointCount] =
    useState<any>(null); // 选择的围栏区域的围栏个数
  const [selectFenceAreaName, setSelectFenceAreaName] = useState<any>(null); // 选择的围栏区域名称
  const [operateInfo, setOperateInfo] = useState<any>({}); // 操作的相关信息
  const [abnormalLoading, setAbnormalLoading] = useState(false); // 查找未配置围栏的loading
  const [abnormalData, setAbnormalData] = useState<any>([]); // 未配置的电子围栏数据
  const [currentPage, setCurrentPage] = useState<number>(0); // 查找未配置围栏时当前定位到的个数
  const [transformLngLat, setTransformLngLat] = useState<string>(''); // 转换的经纬度
  const mapDomRef: any = useRef(null); // 渲染地图的DOM
  const mapRef: any = useRef(null); // 存储地图实例
  const fenceMapOverlayRef: any = useRef({}); // 存储电子围栏相关地图覆盖物
  const selectFenceAreaIdRef: any = useRef(null); // 选择的围栏区域ID
  const markerClustererRef: any = useRef(null); // 聚合点实例
  const drawingManagerRef: any = useRef(null); // 鼠标画圆工具实例
  const drawEndFlagRef: any = useRef(true); // 添加围栏结束(防止按键多次触发多次)
  const fenceAreaListRef: any = useRef(); // 围栏区域列表组件ref
  const hackFunctionsRef: any = useRef(null); // 用于存储劫持的添加、删除覆盖物方法

  // 地图移动完后渲染地图范围内的围栏
  const mapMoveEnd = async () => {
    if (!mapRef.current) return;
    setLoading(true);
    if (selectFenceAreaIdRef.current) {
      clearMap();
      const res = await fenceAreaDetailRequest(selectFenceAreaIdRef.current);
      const fencePoints = res.data?.data?.points || [];
      setSelectFenceAreaPointCount(fencePoints.length);
      if (fencePoints.length) {
        // 以地图边界创建多边形用于判断围栏是否在地图边界内
        const mapBounds = mapRef.current.getBounds();
        const markers: any = [];
        fencePoints.forEach((item: any) => {
          const center = new window.BMap.Point(item.lng, item.lat);
          if (mapBounds.containsPoint(center)) {
            const marker = new window.BMap.Marker(center, {
              icon: new window.BMap.Icon('data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7', new window.BMap.Size(1, 1))
            });
            marker._fenceData = item;
            markers.push(marker);
          }
        });

        markerClustererRef.current = new window.BMapLib.MarkerClusterer(
          mapRef.current,
          { markers }
        );

        // BMapLib.MarkerClusterer 不需要我们实现点击放大逻辑，自身已实现 clusters 点击缩放
      }
    }
    setLoading(false);
  };

  const debounceMapMoveEnd = useDebounce(mapMoveEnd);

  // 渲染每一个电子围栏的详情(拦截透明Marker变为真实圈)
  const _renderClustererItem = (fenceData: any) => {
    const {
      lng,
      lat,
      radius,
      id,
      headingAngleMin,
      headingAngleMax,
      electronicFenceConfigId,
      type
    } = fenceData;
    if (fenceMapOverlayRef.current[id]) {
      return;
    }
    const center = new window.BMap.Point(lng, lat);
    const isAbnormal = !electronicFenceConfigId && !type; // 是否为未配置围栏

    // 生成圆心圆
    const circle = new window.BMap.Circle(center, radius, {
      fillColor: isAbnormal ? '#ff0000' : '#00B2D5',
      strokeColor: isAbnormal ? '#ff0000' : '#00B2D5',
      strokeWeight: 2,
      strokeOpacity: 0.8,
      fillOpacity: 0.4
    });

    circle.addEventListener('mouseover', (e: any) => circleMouseover(e, center));

    // 手动添加到地图，调用未劫持的原始添加覆盖物方法
    hackFunctionsRef.current.originalAddOverlay.call(mapRef.current, circle);

    // 根据最大最小角度计算航向角线的角度和角度浮动区间
    const { angle, angleRange } = getMiddleAngle(headingAngleMin, headingAngleMax);

    // 存储圆形及其编辑器
    fenceMapOverlayRef.current[id] = {
      circle,
      angle,
      angleRange,
      fenceData
    };

    // 生成航向角相关内容
    generateAnglePolyline(center, radius, id);
    // 生成删除的marker
    generateDeleteMarker(center, id);
    // 生成设置动作的marker
    generateActionMarker(center, id);
    // 监听绑定圆形编辑器
    onCircleEditorChange(id);

    // 开启圆的编辑模式
    circle.enableEditing();
  };

  const _destroyClustererItem = (fenceId: number) => {
    const overlayGroup = fenceMapOverlayRef.current[fenceId];
    if (overlayGroup && mapRef.current && hackFunctionsRef.current) {
      overlayGroup.circle?.disableEditing();
      overlayGroup.anglePolyline?.disableEditing();
      if (overlayGroup.circle) hackFunctionsRef.current.originalRemoveOverlay.call(mapRef.current, overlayGroup.circle);
      if (overlayGroup.anglePolyline) hackFunctionsRef.current.originalRemoveOverlay.call(mapRef.current, overlayGroup.anglePolyline);
      if (overlayGroup.actionMarker) hackFunctionsRef.current.originalRemoveOverlay.call(mapRef.current, overlayGroup.actionMarker);
      if (overlayGroup.deleteMarker) hackFunctionsRef.current.originalRemoveOverlay.call(mapRef.current, overlayGroup.deleteMarker);
      delete fenceMapOverlayRef.current[fenceId];
    }
  };

  // 监听N键按下时可以添加围栏
  const handleNPress = (event: any) => {
    if (
      event.code === 'KeyN' &&
      drawEndFlagRef.current &&
      selectFenceAreaIdRef.current
    ) {
      drawFence();
    }
  };

  // 初始化地图
  useEffect(() => {
    importDrawingManagerMap(() => {
      mapRef.current = new window.BMap.Map(mapDomRef.current, { enableMapClick: false });
      mapRef.current.enableScrollWheelZoom();
      mapRef.current.addControl(new window.BMap.ScaleControl({ anchor: window.BMAP_ANCHOR_TOP_LEFT }));
      mapRef.current.addControl(new window.BMap.NavigationControl({ anchor: window.BMAP_ANCHOR_TOP_LEFT }));
      mapRef.current.addControl(new window.BMap.MapTypeControl({ mapTypes: [window.BMAP_NORMAL_MAP, window.BMAP_HYBRID_MAP] }));

      const center = new window.BMap.Point(116.397428, 39.90923);
      mapRef.current.centerAndZoom(center, 16);

      // 劫持 addOverlay/removeOverlay 来同步绘制编辑工具
      const originalAddOverlay = mapRef.current.addOverlay;
      const originalRemoveOverlay = mapRef.current.removeOverlay;

      hackFunctionsRef.current = { originalAddOverlay, originalRemoveOverlay };

      const hookedAddOverlay = function (this: any, overlay: any) {
        originalAddOverlay.call(this, overlay);
        if (overlay instanceof window.BMap.Marker && overlay._fenceData) {
          _renderClustererItem(overlay._fenceData);
        }
      };

      const hookedRemoveOverlay = function (this: any, overlay: any) {
        originalRemoveOverlay.call(this, overlay);
        if (overlay instanceof window.BMap.Marker && overlay._fenceData) {
          _destroyClustererItem(overlay._fenceData.id);
        }
      };

      mapRef.current.addOverlay = hookedAddOverlay;
      mapRef.current.removeOverlay = hookedRemoveOverlay;

      mapRef.current.addEventListener('moveend', debounceMapMoveEnd);
      mapRef.current.addEventListener('zoomend', debounceMapMoveEnd);
    });
    document.addEventListener('keydown', handleNPress);
    registerClipboard('fenceCopyButton');
    return () => {
      if (mapRef.current && hackFunctionsRef.current) {
        mapRef.current.addOverlay = hackFunctionsRef.current.originalAddOverlay;
        mapRef.current.removeOverlay = hackFunctionsRef.current.originalRemoveOverlay;
      }
      mapRef.current = null;
      document.removeEventListener('keydown', handleNPress);
    };
  }, []);

  // 清空地图和所有编辑器
  const clearMap = () => {
    if (markerClustererRef.current) {
      markerClustererRef.current.clearMarkers();
      markerClustererRef.current = null;
    }
    if (drawingManagerRef.current) {
      drawingManagerRef.current.close();
      drawingManagerRef.current = null;
    }
    mapRef.current?.clearOverlays();
    Object.keys(fenceMapOverlayRef.current).forEach((id) => {
      _destroyClustererItem(Number(id));
    });
    fenceMapOverlayRef.current = {};
  };

  // 添加电子围栏点标记
  const upsertFencePoint = async (data: any) => {
    try {
      const res = await upsertFencePointRequest({
        ...data,
        electronicFenceId: selectFenceAreaIdRef.current
      });
      const resData = res.data?.data;
      if (resData) {
        message.success('自动保存成功', 1);
      } else {
        message.error('自动保存失败，请重新调整围栏');
      }
      return resData;
    } catch (error) {
      return false;
    }
  };

  // 删除电子围栏点标记
  const deleteFencePoint = async (id: number) => {
    try {
      const res = await deleteFencePointRequest(id);
      const resData = res.data?.data;
      if (resData) {
        message.success('删除成功', 1);
      } else {
        message.error('删除失败');
      }
      return resData;
    } catch (error) {
      return false;
    }
  };

  // 移入圆圈显示经纬度
  const circleMouseover = async (e: any, center: any) => {
    const lng = center.lng;
    const lat = center.lat;
    const res = await lngLatTransformRequest({ lng, lat });
    const transformLngLat = res.data?.data;
    if (transformLngLat) {
      setTransformLngLat(transformLngLat.join(', '));
    }
  };

  // 获取选择的电子围栏的详细信息
  const getSelectFenceAreaInfo = () => {
    const fenceAreaList = fenceAreaListRef.current?.fenceAreaList || [];
    const fenceArea = fenceAreaList.find(
      (item: any) => item.id === selectFenceAreaIdRef.current
    );
    return {
      defaultMinRadius: fenceArea?.radius || 0,
      headingAngleHalfRange: fenceArea?.headingAngleHalfRange || 0
    };
  };

  // 生成航向角线的编辑器
  const generateAnglePolylineEditor = (path: any, fenceId: number) => {
    // 绘制航向角的线
    const anglePolyline = new window.BMap.Polyline(path, {
      strokeOpacity: 1,
      strokeWeight: 4,
      strokeColor: '#3366FF'
    });
    hackFunctionsRef.current.originalAddOverlay.call(mapRef.current, anglePolyline);

    // 通过给线添加箭头来模拟 showDir (百度API IconSequence)
    const sy = new window.BMap.Symbol(window.BMap_Symbol_SHAPE_BACKWARD_OPEN_ARROW, {
      scale: 0.6,
      strokeColor: '#fff',
      strokeWeight: 2,
    });
    const icons = new window.BMap.IconSequence(sy, '100%', '10%', false);
    anglePolyline.setIcons([icons]);

    anglePolyline.enableEditing();

    // 调整航向角线时调整显示的角度
    anglePolyline.addEventListener('lineupdate', () => {
      const adjustPath = anglePolyline.getPath();
      if (adjustPath.length < 2) return;
      const { angleRange } = fenceMapOverlayRef.current[fenceId];
      const newAngle = calcLatLngAngleBMap(mapRef, adjustPath[0], adjustPath[1]);
      const { headingAngleMin, headingAngleMax } = getMinMaxAngle(
        newAngle,
        angleRange
      );
      upsertFencePoint({
        headingAngleMin,
        headingAngleMax,
        id: fenceId
      });
      fenceMapOverlayRef.current[fenceId].angle = newAngle;
    });

    fenceMapOverlayRef.current[fenceId].anglePolyline = anglePolyline;
  };

  // 生成航向角线相关内容
  const generateAnglePolyline = (
    center: any,
    radius: number,
    fenceId: number
  ) => {
    const { angle } = fenceMapOverlayRef.current[fenceId];
    // 获取航向角线的一个点经纬度
    const ptArr = calcAnotherPoint(
      center.lng,
      center.lat,
      radius,
      angle
    );
    const anglePolylineSecondPoint = new window.BMap.Point(ptArr[0], ptArr[1]);
    generateAnglePolylineEditor([center, anglePolylineSecondPoint], fenceId);
  };

  // 圆形编辑器编辑时绑定的方法
  const onCircleEditorChange = (fenceId: number) => {
    const overlayGroup = fenceMapOverlayRef.current[fenceId];
    if (!overlayGroup) return;
    const { circle, deleteMarker, actionMarker } = overlayGroup;

    // 航向角的线的长度自适应圆的半径
    const _adjustAnglePolyline = (lngLat: any, radius: number) => {
      const { angle, anglePolyline } = fenceMapOverlayRef.current[fenceId];
      const ptArr = calcAnotherPoint(
        lngLat.lng,
        lngLat.lat,
        radius,
        angle
      );
      const anglePolylineSecondPoint = new window.BMap.Point(ptArr[0], ptArr[1]);
      // 调整航向角折线
      anglePolyline.setPath([lngLat, anglePolylineSecondPoint]);
    };

    circle.addEventListener('lineupdate', () => {
      const center = circle.getCenter();
      const radius = circle.getRadius();

      // 控制半径取值范围
      let adjustRadius = radius;
      const { defaultMinRadius } = getSelectFenceAreaInfo();
      if (radius < defaultMinRadius) {
        adjustRadius = defaultMinRadius;
        message.warning(`半径最小为${defaultMinRadius}`);
        circle.setRadius(adjustRadius);
      }
      if (radius > defaultMaxRadius) {
        adjustRadius = defaultMaxRadius;
        message.warning(`半径最大为${defaultMaxRadius}`);
        circle.setRadius(adjustRadius);
      }
      upsertFencePoint({ id: fenceId, lng: center.lng, lat: center.lat, radius: adjustRadius });

      // 调整航向角线
      _adjustAnglePolyline(center, adjustRadius);
      if (deleteMarker) deleteMarker.setPosition(center);
      if (actionMarker) actionMarker.setPosition(center);
    });
  };

  // 显示删除围栏的marker
  const generateDeleteMarker = (center: any, fenceId: number) => {
    const icon = new window.BMap.Icon('https://webapi.amap.com/images/destroy.png', new window.BMap.Size(24, 24));
    const deleteMarker = new window.BMap.Marker(center, {
      icon,
      offset: new window.BMap.Size(-12, 12)
    });

    deleteMarker.addEventListener('click', async () => {
      const deleteRes = await deleteFencePoint(fenceId);
      if (deleteRes && markerClustererRef.current) {
        // 在 MarkerClusterer 中找到并删除 marker
        const markers = markerClustererRef.current.getMarkers();
        const targetMarker = markers.find((m: any) => m._fenceData && m._fenceData.id === fenceId);
        if (targetMarker) {
          markerClustererRef.current.removeMarker(targetMarker);
        } else {
          _destroyClustererItem(fenceId);
        }
      }
    });
    hackFunctionsRef.current.originalAddOverlay.call(mapRef.current, deleteMarker);
    fenceMapOverlayRef.current[fenceId].deleteMarker = deleteMarker;
  };

  // 显示配置动作的marker
  const generateActionMarker = (center: any, fenceId: number) => {
    const icon = new window.BMap.Icon(setPng, new window.BMap.Size(24, 24));
    const actionMarker = new window.BMap.Marker(center, {
      icon,
      offset: new window.BMap.Size(12, -12)
    });

    actionMarker.addEventListener('click', () => {
      const { angle, angleRange } = fenceMapOverlayRef.current[fenceId];
      setOperateInfo({
        type: 'config',
        data: {
          fenceId,
          fenceAreaId: selectFenceAreaIdRef.current,
          angle,
          angleRange
        }
      });
    });
    hackFunctionsRef.current.originalAddOverlay.call(mapRef.current, actionMarker);
    fenceMapOverlayRef.current[fenceId].actionMarker = actionMarker;
  };

  // 选择围栏区域(地图中心定位到第一个电子围栏)
  const onFenceAreaChange = async (checked: boolean, fence: any) => {
    clearMap();
    setAbnormalData([]);
    if (checked) {
      setSelectFenceAreaId(fence.id);
      setSelectFenceAreaName(fence.name);
      selectFenceAreaIdRef.current = fence.id;
      const res = await fenceAreaDetailRequest(fence.id);
      const fencePoints = res.data?.data?.points || [];
      setSelectFenceAreaPointCount(fencePoints.length);
      if (fencePoints.length) {
        const { lng: firstLng, lat: firstLat } = fencePoints[0];
        const pt = new window.BMap.Point(firstLng, firstLat);

        const mapCenter = mapRef.current.getCenter();
        const mapZoom = mapRef.current.getZoom();

        if (Math.abs(mapCenter.lng - firstLng) < 0.00001 && Math.abs(mapCenter.lat - firstLat) < 0.00001 && mapZoom === 18) {
          mapMoveEnd();
        } else {
          mapRef.current.centerAndZoom(pt, 18);
        }
      }
    } else {
      setSelectFenceAreaId(null);
      setSelectFenceAreaName(null);
      selectFenceAreaIdRef.current = null;
    }
  };

  // 绘制电子围栏
  const drawFence = () => {
    if (!drawEndFlagRef.current || !mapRef.current) {
      return;
    }
    const styleOptions = {
      strokeColor: "#00B2D5",
      fillColor: "#00B2D5",
      strokeWeight: 2,
      strokeOpacity: 0.8,
      fillOpacity: 0.4,
    };

    // 如果已有则先销毁
    if (drawingManagerRef.current) {
      drawingManagerRef.current.close();
    }

    const drawingManager = new window.BMapLib.DrawingManager(mapRef.current, {
      isOpen: true,
      enableDrawingTool: false,
      drawingMode: window.BMAP_DRAWING_CIRCLE,
      circleOptions: styleOptions
    });

    drawEndFlagRef.current = false;

    drawingManager.addEventListener('circlecomplete', async (e: any, overlay: any) => {
      // 结束绘制
      drawEndFlagRef.current = true;
      drawingManager.close();

      // 移除自动生成的覆盖物，以便替换为带有绑定的自定义圆
      mapRef.current.removeOverlay(overlay);

      const center = overlay.getCenter();
      const radius = overlay.getRadius();

      let adjustRadius = radius;
      const { defaultMinRadius, headingAngleHalfRange } = getSelectFenceAreaInfo();
      if (radius < defaultMinRadius) {
        adjustRadius = defaultMinRadius;
        message.warning(`半径最小为${defaultMinRadius}`);
      }
      if (radius > defaultMaxRadius) {
        adjustRadius = defaultMaxRadius;
        message.warning(`半径最大为${defaultMaxRadius}`);
      }

      const upsertId = await upsertFencePoint({
        lng: center.lng,
        lat: center.lat,
        headingAngleMin: 360 - headingAngleHalfRange,
        headingAngleMax: 0 + headingAngleHalfRange,
        radius: Number(adjustRadius)
      });

      if (upsertId) {
        const fenceData = {
          lng: center.lng,
          lat: center.lat,
          headingAngleMin: 360 - headingAngleHalfRange,
          headingAngleMax: 0 + headingAngleHalfRange,
          radius: Number(adjustRadius),
          id: upsertId
        };
        const marker = new window.BMap.Marker(center, {
          icon: new window.BMap.Icon('data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7', new window.BMap.Size(1, 1))
        });
        marker._fenceData = fenceData;
        if (markerClustererRef.current) {
          markerClustererRef.current.addMarker(marker);
        } else {
          mapRef.current.addOverlay(marker);
        }
      }
    });

    drawingManagerRef.current = drawingManager;
  };

  // 发布
  const publishFenceArea = async () => {
    const res = await publishFenceAreaRequest(selectFenceAreaId);
    const resData = res?.data?.data;
    if (resData) {
      setOperateInfo({
        type: 'firmware',
        data: {
          type: 'fence',
          resourcePath: resData.preview,
          size: resData.fileSize,
          md5: resData.key
        }
      });
    }
  };

  // 下载围栏文件
  const downloadFenceArea = async () => {
    const res = await publishFenceAreaRequest(selectFenceAreaId);
    const fileUrl = res?.data?.data?.preview;
    if (fileUrl) {
      setOperateInfo({
        type: 'download',
        data: {
          url: fileUrl,
          name: selectFenceAreaName
        }
      });
    }
  };

  // 生成模拟数据
  const mockData = async () => {
    const lng = 117.232194;
    let lat = 38.970915;
    const data = [
      {
        electronicFenceId: selectFenceAreaId,
        lng,
        lat,
        radius: 60,
        headingAngleMin: 60,
        headingAngleMax: 90,
        type: 1,
        straight: 1,
        leftTurn: 1,
        rightTurn: 1,
        angle: 1,
        distance: 20
      }
    ];
    new Array(5000).fill(1).forEach((outerItem: number, outerIndex: number) => {
      lat = NP.plus(lat, outerIndex === 0 ? 0.0012 : 0.0006); // 新的纬度值
      new Array(4).fill(1).forEach((innerItem: number, innerIndex: number) => {
        const currentLng = NP.plus(lng, NP.times(0.0007, innerIndex));
        data.push({
          electronicFenceId: selectFenceAreaId,
          lng: currentLng,
          lat,
          radius: 30,
          headingAngleMin: 60,
          headingAngleMax: 90,
          type: 1,
          straight: 1,
          leftTurn: 1,
          rightTurn: 1,
          angle: 1,
          distance: 20
        });
      });
    });
    const res = await batchUpsertFencePointRequest(data);
    if (res.data?.data) {
      message.success('生成成功');
    }
  };

  // 快速定位
  const onLocation = (value: string) => {
    if (value && mapRef.current) {
      const myGeo = new window.BMap.Geocoder();
      myGeo.getPoint(value, function (point: any) {
        if (point) {
          mapRef.current.centerAndZoom(point, 16);
        } else {
          message.error('根据地址快速定位失败');
        }
      });
    }
  };

  // 查找没有配置的电子围栏
  const findAbnormalFencePoint = async () => {
    try {
      setAbnormalLoading(true);
      const res = await fenceAreaDetailRequest(selectFenceAreaIdRef.current);
      const fencePoints = res.data?.data?.points || [];
      const abnormalFencePoint: any = [];
      fencePoints.forEach((item: any) => {
        const { electronicFenceConfigId, type, lng, lat } = item;
        if (!electronicFenceConfigId && !type) {
          abnormalFencePoint.push({
            lng,
            lat
          });
        }
      });
      setAbnormalData(abnormalFencePoint);
      setCurrentPage(0);
      setAbnormalLoading(false);
    } catch (error) {
      setAbnormalLoading(false);
    }
  };

  // 定位到具体的没有配置的电子围栏
  const onAbnormalFencePointChange = (page: number) => {
    setCurrentPage(page);
    const { lng, lat } = abnormalData[page - 1];
    const pt = new window.BMap.Point(lng, lat);
    mapRef.current.centerAndZoom(pt, 18);
  };

  return (
    <div className={styles.crossMark}>
      <FenceAreaList
        selectFenceAreaId={selectFenceAreaId}
        setSelectFenceAreaId={(id: null) => {
          setSelectFenceAreaId(id);
          selectFenceAreaIdRef.current = id;
        }}
        clearMap={() => {
          clearMap();
          setAbnormalData([]);
        }}
        onFenceAreaChange={onFenceAreaChange}
        ref={fenceAreaListRef}
        selectFenceAreaPointCount={selectFenceAreaPointCount}
      />
      <div className={styles.rightPart}>
        <div className={styles.title}>
          <Input.Search
            placeholder="输入地址可快速定位到位置"
            style={{ width: '280px' }}
            allowClear
            enterButton="定位"
            onSearch={onLocation}
          />
          <PermissionButton
            onClick={drawFence}
            disabled={!selectFenceAreaId}
            operateType="/car-admin/electron-fence/add-fence"
          >
            添加围栏
          </PermissionButton>
          {/* <PermissionButton
            onClick={publishFenceArea}
            type="primary"
            disabled={!selectFenceAreaId}
            operateType="/car-admin/electron-fence/publish-fence"
          >
            发布
          </PermissionButton> */}
          <PermissionButton
            onClick={downloadFenceArea}
            type="primary"
            disabled={!selectFenceAreaId}
            operateType="/car-admin/electron-fence/download-fence"
          >
            下载文件
          </PermissionButton>
          {/* <PermissionButton
            onClick={mockData}
            type="primary"
            disabled={!selectFenceAreaId}
          >
            生成模拟数据
          </PermissionButton> */}
          <PermissionButton
            onClick={findAbnormalFencePoint}
            type="primary"
            disabled={!selectFenceAreaId}
            loading={abnormalLoading}
          >
            查找未配置围栏
          </PermissionButton>
          {Boolean(abnormalData.length) && (
            <Pagination
              defaultCurrent={0}
              total={abnormalData.length}
              defaultPageSize={1}
              simple
              onChange={onAbnormalFencePointChange}
              current={currentPage}
            />
          )}
          {transformLngLat}
          <Button
            style={{ visibility: transformLngLat ? 'visible' : 'hidden' }}
            type="link"
            icon={<CopyOutlined />}
            data-clipboard-text={transformLngLat}
            id="fenceCopyButton"
          />
        </div>
        <LoadingWrapper spinning={loading} autoHeight>
          <div ref={mapDomRef} style={{ height: '100%' }} />
        </LoadingWrapper>
      </div>
      {operateInfo.type === 'firmware' && (
        <FirmwareAddModal
          fileData={operateInfo.data}
          closeModal={() => setOperateInfo({})}
        />
      )}
      {operateInfo.type === 'download' && (
        <DownloadModal
          data={operateInfo.data}
          closeModal={() => setOperateInfo({})}
        />
      )}
      {/* 配置电子围栏 */}
      {operateInfo.type === 'config' && (
        <FenceConfigModal
          {...operateInfo.data}
          closeModal={() => setOperateInfo({})}
          configSuccessCallback={(angleRange: number, fenceId: number) => {
            fenceMapOverlayRef.current[fenceId].angleRange = angleRange;
            fenceMapOverlayRef.current[fenceId].circle.setFillColor('#00B2D5');
            fenceMapOverlayRef.current[fenceId].circle.setStrokeColor('#00B2D5');
          }}
        />
      )}
    </div>
  );
};

export default ElectronFence;
